import { Link } from 'react-router-dom';
import { usePaperTheme } from '@/hooks/usePaperTheme';

export function TermsPage() {
  usePaperTheme();
  return (
    <LegalShell title="Terms of Service" updated="2026-04-23">
      <P>
        By creating a Clyra account or using the service at{' '}
        <Code>useclyra.com</Code> ("Clyra", "we", "us"), you agree to
        these terms. If you don't agree, stop using the service.
      </P>

      <H>1. The service</H>
      <P>
        Clyra is a cloud-architecture diagramming tool that generates diagrams
        and infrastructure-as-code scaffolds from natural-language prompts.
        Output is a best-effort draft, not a production-ready deployment — you
        are responsible for reviewing any generated diagram or code before
        using it.
      </P>

      <H>2. Accounts</H>
      <P>
        You need a valid email and a password of at least 8 characters, or a
        Google / GitHub account with a verified email. You're responsible for
        keeping your login credentials safe. Tell us if your account is
        compromised. One person, one account.
      </P>

      <H>3. Acceptable use</H>
      <P>Don't abuse the service. Specifically:</P>
      <ul className="ml-5 list-disc space-y-1 text-charcoal-muted">
        <li>No automated scraping or load-testing without written permission.</li>
        <li>No attempts to bypass rate limits, plan gates, or billing.</li>
        <li>No prompts that ask the model to produce illegal content, malware, or material depicting minors.</li>
        <li>No reselling or sublicensing the service.</li>
      </ul>
      <P>We may suspend accounts that do this without refund.</P>

      <H>4. Plans and billing</H>
      <P>
        Free, Pro, and BYO plans are described on the{' '}
        <A href="/#pricing">pricing section</A>.
        Paid plans renew monthly. Nigerian customers are billed in NGN via
        Paystack; everyone else is billed in USD via Polar, who acts as the
        Merchant of Record (they handle tax/VAT). Charges appear on your
        statement as "POLAR" or "PAYSTACK" respectively.
      </P>
      <P>
        You may cancel at any time from Settings. If you cancel within{' '}
        <strong>24 hours</strong> of upgrading, we refund the charge in full.
        After that, the plan runs until the end of the current billing period
        and then reverts to Free — no partial refund.
      </P>

      <H>5. Your content</H>
      <P>
        Diagrams you create belong to you. We store them so you can open them
        later. We do not train models on your diagrams or prompts. You grant
        us the non-exclusive right to process your content for the sole
        purpose of running the service (generating, storing, displaying it
        back to you).
      </P>

      <H>6. BYO plan</H>
      <P>
        On the BYO plan you provide your own API key for OpenAI, Anthropic,
        or Gemini. Your key is used only to execute your own requests and is
        never used on behalf of another user. Costs billed by your model
        provider are your responsibility.
      </P>

      <H>7. Availability</H>
      <P>
        We target high availability but offer no SLA on free plans. Paid
        plans during public beta are best-effort; we'll publish an SLA before
        we raise prices.
      </P>

      <H>8. Termination</H>
      <P>
        You can delete your account at any time. We can suspend or terminate
        accounts that violate these terms. On termination we delete your data
        within 30 days unless we're legally required to retain it.
      </P>

      <H>9. Liability</H>
      <P>
        The service is provided "as is". To the maximum extent allowed by law,
        our total liability to you for any claim is capped at what you paid us
        in the 12 months preceding the claim, or USD 50 if you paid nothing.
      </P>

      <H>10. Changes</H>
      <P>
        We can update these terms. If we make a material change we'll notify
        you by email or in-app. Continuing to use the service after the change
        counts as acceptance.
      </P>

      <H>11. Contact</H>
      <P>
        Questions:{' '}
        <A href="mailto:oche@useclyra.com">oche@useclyra.com</A>
      </P>

      <P className="mt-12 text-tiny text-charcoal-faint">
        ← <A href="/">Back to Clyra</A>
      </P>
    </LegalShell>
  );
}

function LegalShell({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-paper text-charcoal-soft">
      <nav className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-lg font-semibold tracking-tight-ish text-charcoal">
          clyra<span className="text-teal-500">.</span>
        </Link>
        <Link to="/login" className="text-petit text-charcoal-muted hover:text-charcoal transition-colors">
          Sign in
        </Link>
      </nav>
      <main className="mx-auto max-w-3xl px-6 pb-24 pt-8">
        <p className="text-petit font-medium uppercase tracking-[0.14em] text-teal-600">Legal</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-display text-charcoal">{title}</h1>
        <p className="mt-2 text-petit text-charcoal-faint">Last updated {updated}</p>
        <div className="mt-10 space-y-5 text-sm leading-relaxed text-charcoal-muted">
          {children}
        </div>
      </main>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="pt-6 text-base font-semibold text-charcoal">{children}</h2>;
}

function P({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={className}>{children}</p>;
}

function A({ href, children }: { href: string; children: React.ReactNode }) {
  if (href.startsWith('/') || href.startsWith('#')) {
    return (
      <Link to={href} className="text-teal-600 hover:text-teal-500 transition-colors">
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className="text-teal-600 hover:text-teal-500 transition-colors">
      {children}
    </a>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded border border-stone-subtle bg-paper-soft px-1.5 py-0.5 font-mono text-tiny text-charcoal">
      {children}
    </code>
  );
}

export { LegalShell };
